import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiAuthError } from '../lib/api';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { Badge, Button, Input } from '../atoms';
import { DataTable, CreateEntityModal, Modal, useToast, CustomFieldDefinitionModal, dataTypeLabel } from '../organisms';
import type { CustomFieldDataType, CustomFieldDefinition } from '../organisms';

/**
 * CustomFieldsAdmin — /custom-fields
 *
 * The per-business custom-field definition registry: the anti-cookie-cutter
 * control center. Pick a business to see and manage the field schema that
 * belongs to it. A field defined for one business is invisible to every other.
 *
 * Scope: ONE level. A definition belongs to a business (`custom_field_definitions
 * .client_id` — the DB table is still named `clients`) and applies to every
 * Ignite record that business owns: its people, assessments, referrals,
 * enrolments, sessions and attendance (services/custom-fields
 * CUSTOM_FIELD_HOST_TABLES is the source of truth).
 *
 * Definition LISTING + edit + activate/deactivate ride the generic CRUD table
 * 'custom_field_definitions' (GET/PUT /api/custom-field-definitions), filtered
 * by client_id and sorted by display_order.
 *
 * Definition LIFECYCLE rides the dedicated endpoints, which the generic CRUD
 * surface can't express:
 *   POST /api/custom-fields/definitions          — create w/ server key-gen + validation
 *   PUT  /api/custom-fields/definitions/reorder   — atomic display_order reorder
 *   POST /api/custom-fields/definitions/ingest    — bulk-create from a spreadsheet
 *
 * Gated to super_admin/admin.
 */

interface CurrentUser {
  role: string;
}

interface BusinessRow {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
}

interface IngestResult {
  created: CustomFieldDefinition[];
  skipped: Array<{ field_key: string; label: string; reason: string }>;
  columnCount: number;
}

const SCOPE_LABEL = 'this business';

/**
 * The definitions list key. Kept at three elements — ['custom-field-definitions',
 * businessId, ''] — because the in-profile CustomFieldsPanel uses that exact
 * shape, so the two surfaces share one cache entry and stay in lockstep.
 */
const defsKey = (businessId: string) => ['custom-field-definitions', businessId, ''];

export function CustomFieldsAdmin() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [business, setBusiness] = useState<BusinessRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const businessId = business?.id ?? '';

  const userQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  const definitionsQuery = useQuery<CustomFieldDefinition[]>({
    queryKey: defsKey(businessId),
    queryFn: ({ signal }) => api.get<CustomFieldDefinition[]>(
      `/api/custom-field-definitions?client_id=${encodeURIComponent(businessId)}&sort=display_order&dir=asc`, signal,
    ),
    enabled: !!businessId,
  });

  const invalidateDefs = () => {
    queryClient.invalidateQueries({ queryKey: defsKey(businessId) });
    // The reuse catalog on the create modal lists every definition in the org.
    queryClient.invalidateQueries({ queryKey: ['custom-field-catalog'] });
  };

  const toggleActive = useMutation({
    mutationFn: (def: CustomFieldDefinition) =>
      api.put(`/api/custom-field-definitions/${def.id}`, { is_active: !def.is_active }),
    onSuccess: (_data, def) => {
      invalidateDefs();
      toast({ variant: 'success', message: `Field ${def.is_active ? 'deactivated' : 'reactivated'}.` });
    },
    onError: () => toast({ variant: 'error', message: 'Could not update the field.' }),
  });

  // Atomic reorder — the whole business's id list in the new order.
  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put('/api/custom-fields/definitions/reorder', { ordered_ids: orderedIds }),
    onSuccess: () => invalidateDefs(),
    onError: (err) => toast({ variant: 'error', message: err instanceof Error ? err.message : 'Reorder failed.' }),
  });

  function move(id: string, dir: -1 | 1) {
    const defs = definitionsQuery.data ?? [];
    const ids = defs.map(d => d.id);
    const idx = ids.indexOf(id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    reorder.mutate(ids);
  }

  if (userQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  }

  if (userQuery.data && userQuery.data.role !== 'super_admin' && userQuery.data.role !== 'admin' && userQuery.data.role !== 'administrator') {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>Custom field administration is restricted to super_admin and admin roles.</p>
      </div>
    );
  }

  const definitions = definitionsQuery.data ?? [];

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Custom fields</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Define the fields that make each business&rsquo;s records theirs. A field added for one business
          stays invisible to every other, and applies to that business&rsquo;s people, assessments,
          referrals and enrolments.
        </p>
      </header>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--sp-3)',
        alignItems: 'flex-end',
        marginBottom: 'var(--sp-5)',
      }}>
        <BusinessPicker selected={business} onSelect={setBusiness} />
        {businessId && (
          <>
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              + Add field
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>
              Import from spreadsheet
            </Button>
          </>
        )}
      </div>

      {!businessId ? (
        <Empty message="Search for a business to see and manage its custom fields." />
      ) : (
        <DefinitionsTable
          definitions={definitions}
          loading={definitionsQuery.isLoading}
          error={definitionsQuery.error as Error | null}
          onEdit={(id) => setEditId(id)}
          onToggleActive={(def) => toggleActive.mutate(def)}
          onMove={move}
          busy={toggleActive.isPending || reorder.isPending}
        />
      )}

      <CustomFieldDefinitionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clientId={businessId}
        scopeLabel={SCOPE_LABEL}
        existingKeys={definitions.map(d => d.field_key)}
        onCreated={() => { invalidateDefs(); setCreateOpen(false); }}
      />

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        businessId={businessId}
        onIngested={() => invalidateDefs()}
      />

      <CreateEntityModal
        open={!!editId}
        onClose={() => setEditId(null)}
        table="custom_field_definitions"
        title="Edit custom field"
        recordId={editId ?? undefined}
        invalidateKeys={[defsKey(businessId), ['custom-field-catalog']]}
      />
    </>
  );
}

/**
 * BusinessPicker — a debounced server-side typeahead over /api/clients.
 *
 * Not the shared SearchableSelect atom, which filters an in-memory option list:
 * the generic-CRUD list endpoint clamps `limit` to 200 and this org holds
 * thousands of businesses, so an in-memory picker could only ever reach the
 * first page and would silently hide the rest. The server's `q` filter (LIKE
 * over clients' SEARCH_COLUMNS) is the only way to reach all of them.
 */
function BusinessPicker({
  selected, onSelect,
}: {
  selected: BusinessRow | null;
  onSelect: (b: BusinessRow | null) => void;
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebouncedValue(term, 300);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Close the result list on an outside click so it doesn't hang over the table.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const query = debounced.trim();
  const resultsQuery = useQuery<{ rows: BusinessRow[]; total: number }>({
    queryKey: ['custom-fields-business-search', query],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ limit: '25', sort: 'name', dir: 'asc' });
      if (query) qs.set('q', query);
      const resp = await api.get<BusinessRow[] | { rows: BusinessRow[]; total: number }>(
        `/api/clients?${qs.toString()}`, signal,
      );
      return Array.isArray(resp) ? { rows: resp, total: resp.length } : resp;
    },
    enabled: open,
  });

  const rows = resultsQuery.data?.rows ?? [];
  const total = resultsQuery.data?.total ?? 0;

  if (selected) {
    return (
      <FilterField label="Business">
        <div style={{
          display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          padding: '8px 12px',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
          background: 'var(--color-card)',
          minHeight: 38,
        }}>
          <span style={{
            color: 'var(--color-ink)', fontSize: 'var(--fs-md)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>{selected.name}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onSelect(null); setTerm(''); setOpen(true); }}
          >Change</Button>
        </div>
      </FilterField>
    );
  }

  return (
    <div ref={boxRef} style={{ position: 'relative', minWidth: 320 }}>
      <FilterField label="Business">
        <Input
          value={term}
          placeholder="Search businesses by name, city or category…"
          onFocus={() => setOpen(true)}
          onChange={e => { setTerm(e.target.value); setOpen(true); }}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
          aria-label="Search businesses"
        />
      </FilterField>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
          boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.28))',
          maxHeight: 320, overflowY: 'auto',
        }}>
          {resultsQuery.isLoading ? (
            <PickerNote>Searching…</PickerNote>
          ) : resultsQuery.error ? (
            <PickerNote>Could not load businesses.</PickerNote>
          ) : rows.length === 0 ? (
            <PickerNote>{query ? `No business matches “${query}”.` : 'No businesses yet.'}</PickerNote>
          ) : (
            <>
              {rows.map(b => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => { onSelect(b); setOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: 'var(--sp-2) var(--sp-3)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    font: 'inherit', color: 'var(--color-ink)',
                  }}
                >
                  <span style={{ display: 'block' }}>{b.name}</span>
                  {(b.city || b.state) && (
                    <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
                      {[b.city, b.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                </button>
              ))}
              {total > rows.length && (
                <PickerNote>
                  Showing {rows.length} of {total} matches — keep typing to narrow.
                </PickerNote>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PickerNote({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      margin: 0, padding: 'var(--sp-3)',
      color: 'var(--color-muted)', fontSize: 'var(--fs-sm)',
    }}>{children}</p>
  );
}

/**
 * BulkImportModal — upload a CSV/TSV and bulk-create a definition per header
 * column. Calls POST /api/custom-fields/definitions/ingest, which parses the
 * sheet with the frozen server parser, infers each column's type, and creates
 * the definitions on this business (idempotent — existing keys are skipped).
 */
function BulkImportModal({
  open, onClose, businessId, onIngested,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  onIngested: () => void;
}) {
  const { toast } = useToast();
  const [fileName, setFileName] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<IngestResult | null>(null);

  function reset() {
    setFileName(''); setSourceText(''); setError(null); setResult(null);
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    setError(null); setResult(null); setSourceText(''); setFileName('');
    if (!f) return;
    // Binary .xlsx is not supported by the shipped parser (text backend only).
    if (/\.xlsx?$/i.test(f.name)) {
      setError('Binary Excel (.xlsx) is not yet supported — export the sheet as CSV or TSV first.');
      return;
    }
    const text = await f.text();
    if (text.includes('\u0000') || text.startsWith('PK')) {
      setError('This looks like a binary file. Export the sheet as CSV or TSV first.');
      return;
    }
    setFileName(f.name);
    setSourceText(text);
  }

  async function submit() {
    if (!sourceText) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<IngestResult>('/api/custom-fields/definitions/ingest', {
        source: { text: sourceText },
        client_id: businessId,
      });
      setResult(res);
      onIngested();
      toast({
        variant: 'success',
        message: `${res.created.length} field${res.created.length === 1 ? '' : 's'} created for ${SCOPE_LABEL}.`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+: /, '') : 'Import failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Import fields from a spreadsheet"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={submitting}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button variant="primary" onClick={submit} disabled={submitting || !sourceText}>
              {submitting ? 'Creating…' : 'Create fields'}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          Each column header in the file becomes a custom field for {SCOPE_LABEL}, with its type inferred
          from the data. Columns that already exist are skipped. Choose a .csv or .tsv file
          (binary .xlsx is not yet supported — export to CSV first).
        </p>
        <input type="file" accept=".csv,.tsv,.txt" onChange={onPickFile} style={{ fontFamily: 'inherit' }} />
        {fileName && !result && (
          <Badge variant="success" dot>{fileName}</Badge>
        )}
        {error && (
          <div style={{
            padding: 'var(--sp-3)', background: 'var(--color-status-red-bg)',
            border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
            borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)',
          }}>{error}</div>
        )}
        {result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <p style={{ margin: 0, color: 'var(--color-ink)' }}>
              <strong>{result.created.length}</strong> field{result.created.length === 1 ? '' : 's'} created
              {' '}· <strong>{result.skipped.length}</strong> skipped
              {' '}(of {result.columnCount} column{result.columnCount === 1 ? '' : 's'}).
            </p>
            {result.created.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {result.created.map(d => (
                  <Badge key={d.id} variant="success">{d.label} · {dataTypeLabel[d.data_type]}</Badge>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {result.skipped.map((s, i) => (
                  <Badge key={`${s.field_key}-${i}`} variant="neutral">{s.label} · {s.reason}</Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function DefinitionsTable({
  definitions, loading, error, onEdit, onToggleActive, onMove, busy,
}: {
  definitions: CustomFieldDefinition[];
  loading: boolean;
  error: Error | null;
  onEdit: (id: string) => void;
  onToggleActive: (def: CustomFieldDefinition) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  busy: boolean;
}) {
  const columns = useMemo<ColumnDef<CustomFieldDefinition>[]>(() => [
    {
      id: 'order',
      header: 'Order',
      cell: ({ row, table }) => {
        const count = table.getRowModel().rows.length;
        const isFirst = row.index === 0;
        const isLast = row.index === count - 1;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span style={{ color: 'var(--color-muted)', minWidth: 16 }}>{row.original.display_order}</span>
            <div style={{ display: 'flex', gap: 2 }}>
              <Button variant="ghost" size="sm" disabled={busy || isFirst} onClick={() => onMove(row.original.id, -1)} aria-label="Move up">↑</Button>
              <Button variant="ghost" size="sm" disabled={busy || isLast} onClick={() => onMove(row.original.id, 1)} aria-label="Move down">↓</Button>
            </div>
          </div>
        );
      },
    },
    { accessorKey: 'label', header: 'Label' },
    {
      accessorKey: 'field_key',
      header: 'Key',
      cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{getValue() as string}</span>,
    },
    {
      accessorKey: 'data_type',
      header: 'Type',
      cell: ({ getValue }) => <Badge variant="neutral">{dataTypeLabel[getValue() as CustomFieldDataType]}</Badge>,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ getValue }) => ((getValue() as boolean)
        ? <Badge variant="success" dot>Active</Badge>
        : <Badge variant="neutral" dot>Inactive</Badge>),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={() => onEdit(row.original.id)}>Edit</Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onToggleActive(row.original)}>
            {row.original.is_active ? 'Deactivate' : 'Reactivate'}
          </Button>
        </div>
      ),
    },
  ], [onEdit, onToggleActive, onMove, busy]);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading fields…</p>;
  if (error) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load custom fields. {error.message}
      </div>
    );
  }
  if (definitions.length === 0) {
    return <Empty message={`No custom fields for ${SCOPE_LABEL} yet. Add the first one.`} />;
  }
  return <DataTable data={definitions} columns={columns} />;
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 220 }}>
      <span style={{
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</span>
      {children}
    </label>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}
